import {useState} from 'react'
import type {Moment} from 'moment-timezone'
import {
  defaultStartDate,
  defaultEndDate
} from '@freckle/educator-entities/ts/common/helpers/date-helper'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {H1} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {DateRangePicker} from '@freckle/school/ts/common/components/common/date-range-picker'
import {IdpPicker} from '@freckle/school/ts/common/components/common/idp-picker'
import {PeriscopeEmbed} from '@freckle/school/ts/common/components/common/periscope-embed'
import {PracticeTypePicker} from '@freckle/school/ts/common/components/common/practice-type-picker'
import {SchoolPicker} from '@freckle/school/ts/common/components/common/school-picker'
import type {
  AllProvidersOrSpecificT,
  PracticeTypeFilterT
} from '@freckle/school/ts/common/helpers/api-helper'
import {GradePicker} from '@freckle/school/ts/pages/growth/components/grade-picker'
import {dayEnd, dayStart} from '../../common/components/common/date-range-picker/logic'
import type {SchoolAdminAttrs} from '../../users/models/school-admin'
import {usageOverview, filters, printButton} from './usage.module.scss'

type Props = {
  schools: Array<SchoolAttrs>
  initSchoolId: number | undefined
  onChangeSchool: (a: number) => void
  onAllSchools: () => void
  schoolAdmin: SchoolAdminAttrs
}

export function Usage(props: Props) {
  const {schools, initSchoolId, onChangeSchool, onAllSchools, schoolAdmin} = props
  const {t} = useSafeTranslation()

  const [schoolId, setSchoolId] = useState<number | undefined>(initSchoolId)
  const [grade, setGrade] = useState<number | undefined | null>()
  const [idpOrigins, setIdpOrigins] = useState<AllProvidersOrSpecificT>({tag: 'all'})
  const [practiceTypes, setPracticeTypes] = useState<PracticeTypeFilterT>({tag: 'none'})
  const [startDate, setStartDate] = useState<Moment>(defaultStartDate)
  const [endDate, setEndDate] = useState<Moment>(defaultEndDate)

  const periscopeEmbedData = {
    tag: 'school_usage' as const,
    schoolId,
    grade,
    idpOrigins,
    practiceTypes,
    startDate: dayStart(startDate),
    endDate: dayEnd(endDate)
  }

  const [currentSchoolId, showingAllSchools] =
    schoolId !== undefined ? [schoolId, false] : [0, true]

  return (
    <div className={usageOverview}>
      <H1>{t('USAGE_H1_HEADING')}</H1>
      <Button className={printButton} style="secondary" onClick={window.print}>
        <i className="fa fa-print" /> {t('USAGE_BUTTON_PRINT')}
      </Button>
      <div className={filters}>
        <SchoolPicker
          schools={schools}
          currentSchoolId={currentSchoolId}
          onChange={schoolId => {
            onChangeSchool(schoolId)
            setSchoolId(schoolId)
          }}
          onAllSchools={() => {
            onAllSchools()
            setSchoolId(undefined)
          }}
          showingAllSchools={showingAllSchools}
        />
        <GradePicker
          currentGrade={grade}
          onChangeGrade={grade => {
            setGrade(grade)
          }}
        />
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChangeStartDate={setStartDate}
          onChangeEndDate={setEndDate}
        />
        <IdpPicker
          allSchoolIds={schools.map(s => s.id)}
          adminId={schoolAdmin.id}
          currentIdps={idpOrigins}
          onChange={setIdpOrigins}
        />
        <PracticeTypePicker selected={practiceTypes} onChange={setPracticeTypes} />
      </div>
      <PeriscopeEmbed data={periscopeEmbedData} />
    </div>
  )
}
